/**
 * Self-test suite for the cloud-sync subsystem.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/CloudSyncSelfTest.swift` case-for-case,
 * with the iOS Curve25519/CryptoKit calls replaced by injected mock signer
 * and verifier closures so this suite does NOT depend on the global
 * identity-key store (dependency injection is the spec'd shape).
 *
 * INVARIANTS COVERED
 *   - Manifest canonical bytes are deterministic (encode twice → identical).
 *   - Canonical bytes change when itemKind, itemVersion, blobSha256, or ts
 *     changes (commitment is sensitive to every signed field).
 *   - Canonical bytes for any plausible manifest fit under 256 bytes —
 *     proves "10MB blob → ≤256B canonical input" even though manifests
 *     here don't carry the blob inline.
 *   - Cipher: seal+open round-trip recovers the same plaintext.
 *   - Cipher: tampered blob → SHA-256 mismatch caught BEFORE AES-GCM-open.
 *   - Cipher: forged manifest (bad signature) → fails with
 *     `kind === 'invalidSignature'`.
 *   - Cipher: itemKind mismatch between expectedKind and manifest →
 *     fails with `kind === 'kindMismatch'`.
 *   - Conflict policy: every branch (itemVersion, ts, authorDeviceId,
 *     identical-manifests) returns the documented winner + reason string.
 *   - Store: applyRemote then currentBlob retrieves identical bytes; clear
 *     removes the entry.
 *   - Store: two instances on the same DB name share state (SW/main
 *     boundary).
 *
 * Registered with the global SelfTestRunner singleton at module load time;
 * auto-discovered by `selftest/index.ts` via Vite `import.meta.glob`.
 *
 * `isVerified: true` — pure-logic layer (codec, manifest, cipher, conflict
 * resolution) verified against Chrome on Windows 2026-05-21. KDS server
 * endpoints and the reconcile-loop integration are out-of-scope for this
 * suite and tracked separately.
 *
 * @module CloudSyncSelfTest
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  canonicalManifestBytes,
  CLOUD_SYNC_CANONICAL_MAX_BYTES,
  type CloudSyncItemKind,
  type CloudSyncManifest,
} from "./CloudSyncRecord";
import {
  CloudSyncCipherError,
  openItem,
  sealItem,
  sha256,
  type ManifestSigner,
  type ManifestVerifier,
} from "./CloudSyncCipher";
import { resolveConflict } from "./CloudSyncConflictPolicy";
import { CloudSyncStore } from "./CloudSyncStore";

// ---------------------------------------------------------------------------
// Mock signer / verifier — pretend Ed25519 over canonical bytes
// ---------------------------------------------------------------------------

/**
 * The cipher API is parametric in the Ed25519 implementation: it just
 * needs `(canonical) → 64-byte signature` and a verifier. For the self-
 * test we use a deterministic mock keyed by a fake "device identity"
 * byte — sufficient to exercise:
 *
 *   - "good signer matches good verifier" → openItem succeeds
 *   - "tampered signature" → verifier returns false → invalidSignature
 *   - "wrong device" → verifier rejects → invalidSignature
 *
 * Real Ed25519 is exercised in the identity-key suite, not here.
 */
function makeMockSignerForDevice(
  deviceId: number,
): { signer: ManifestSigner; verifier: ManifestVerifier } {
  // The "signature" is `sha256(canonical || deviceId-byte || canonical)`,
  // a deterministic 32-byte value padded to 64. Not cryptographically
  // sound — we just need a consistent (canonical, deviceId) → sig mapping
  // for the test fixture.
  const deriveSig = async (
    canonical: Uint8Array,
    forDeviceId: number,
  ): Promise<Uint8Array> => {
    const composite = new Uint8Array(canonical.byteLength * 2 + 4);
    composite.set(canonical, 0);
    new DataView(composite.buffer).setUint32(canonical.byteLength, forDeviceId, true);
    composite.set(canonical, canonical.byteLength + 4);
    const digest = await sha256(composite);
    const padded = new Uint8Array(64);
    padded.set(digest, 0);
    padded.set(digest, 32);
    return padded;
  };

  const signer: ManifestSigner = (canonical) => deriveSig(canonical, deviceId);
  const verifier: ManifestVerifier = async (
    canonical,
    signature,
    authorDeviceId,
  ) => {
    if (signature.byteLength !== 64) return false;
    const expected = await deriveSig(canonical, authorDeviceId);
    if (expected.byteLength !== signature.byteLength) return false;
    let diff = 0;
    for (let i = 0; i < expected.byteLength; i += 1) {
      diff |= expected[i] ^ signature[i];
    }
    return diff === 0;
  };
  return { signer, verifier };
}

/** A verifier that rejects everything — for forged-signature tests. */
const rejectAllVerifier: ManifestVerifier = async () => false;

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

function freshStore(): CloudSyncStore {
  dbCounter += 1;
  return new CloudSyncStore(
    `kaether:cloud-sync:selftest:${dbCounter}:${Date.now()}`,
  );
}

function freshRootKey(): Uint8Array {
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  return buf;
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// A. Manifest canonical bytes
// ---------------------------------------------------------------------------

async function testCanonicalBytesStable(): Promise<TestCase> {
  const name = "manifest: canonical bytes are stable across encodings";
  try {
    const blobSha256 = await sha256(new Uint8Array([1, 2, 3]));
    const m: CloudSyncManifest = {
      itemKind: "settings",
      itemVersion: 7,
      blobSha256,
      authorDeviceId: 42,
      ts: 1_700_000_000_000,
    };
    const a = canonicalManifestBytes(m);
    const b = canonicalManifestBytes(m);
    return bytesEqual(a, b)
      ? pass(name)
      : fail(name, "two encodings produced different bytes");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCanonicalBytesDifferOnItemKind(): Promise<TestCase> {
  const name = "manifest: canonical bytes differ when itemKind changes";
  try {
    const blobSha256 = await sha256(new Uint8Array([1, 2, 3]));
    const base: CloudSyncManifest = {
      itemKind: "settings",
      itemVersion: 1,
      blobSha256,
      authorDeviceId: 1,
      ts: 100,
    };
    const a = canonicalManifestBytes(base);
    const b = canonicalManifestBytes({ ...base, itemKind: "blockList" });
    return !bytesEqual(a, b)
      ? pass(name)
      : fail(name, "changing itemKind did not change canonical bytes");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCanonicalBytesDifferOnItemVersion(): Promise<TestCase> {
  const name = "manifest: canonical bytes differ when itemVersion changes";
  try {
    const blobSha256 = await sha256(new Uint8Array([1, 2, 3]));
    const base: CloudSyncManifest = {
      itemKind: "settings",
      itemVersion: 1,
      blobSha256,
      authorDeviceId: 1,
      ts: 100,
    };
    const a = canonicalManifestBytes(base);
    const b = canonicalManifestBytes({ ...base, itemVersion: 2 });
    return !bytesEqual(a, b)
      ? pass(name)
      : fail(name, "changing itemVersion did not change canonical bytes");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCanonicalBytesDifferOnBlobSha(): Promise<TestCase> {
  const name = "manifest: canonical bytes differ when blobSha256 changes";
  try {
    const hashA = await sha256(new Uint8Array([1, 2, 3]));
    const hashB = await sha256(new Uint8Array([4, 5, 6]));
    const a = canonicalManifestBytes({
      itemKind: "settings",
      itemVersion: 1,
      blobSha256: hashA,
      authorDeviceId: 1,
      ts: 100,
    });
    const b = canonicalManifestBytes({
      itemKind: "settings",
      itemVersion: 1,
      blobSha256: hashB,
      authorDeviceId: 1,
      ts: 100,
    });
    return !bytesEqual(a, b)
      ? pass(name)
      : fail(name, "changing blobSha256 did not change canonical bytes");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCanonicalBytesDifferOnTs(): Promise<TestCase> {
  const name = "manifest: canonical bytes differ when ts changes";
  try {
    const blobSha256 = await sha256(new Uint8Array([1, 2, 3]));
    const base: CloudSyncManifest = {
      itemKind: "settings",
      itemVersion: 1,
      blobSha256,
      authorDeviceId: 1,
      ts: 100,
    };
    const a = canonicalManifestBytes(base);
    const b = canonicalManifestBytes({ ...base, ts: 200 });
    return !bytesEqual(a, b)
      ? pass(name)
      : fail(name, "changing ts did not change canonical bytes");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCanonicalBytesBoundedBySha256(): Promise<TestCase> {
  const name = "manifest: canonical bytes ≤256 even for large blobs";
  try {
    // The point isn't that we feed a big blob in (we don't — manifest
    // only carries the hash). It's that the canonical surface stays
    // bounded regardless of blob size: 1 + 1 + 8 + 1 + 32 + 1 + 4 + 1 + 8 = 57.
    const blobSha256 = await sha256(new Uint8Array(10 * 1024 * 1024)); // 10MB
    const bytes = canonicalManifestBytes({
      itemKind: "contactRoster",
      itemVersion: 999,
      blobSha256,
      authorDeviceId: 0xdeadbeef >>> 0,
      ts: 1_700_000_000_000,
    });
    return bytes.byteLength <= CLOUD_SYNC_CANONICAL_MAX_BYTES
      ? pass(name)
      : fail(
          name,
          `canonical bytes = ${bytes.byteLength}, want ≤${CLOUD_SYNC_CANONICAL_MAX_BYTES}`,
        );
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// B. Cipher
// ---------------------------------------------------------------------------

async function testCipherRoundTrip(): Promise<TestCase> {
  const name = "cipher: seal+open round-trip recovers identical plaintext";
  try {
    const rootKey = freshRootKey();
    const { signer, verifier } = makeMockSignerForDevice(1);
    const plaintext = new TextEncoder().encode(
      "notification settings v1: muted threads = [a, b]",
    );
    const sealed = await sealItem(rootKey, "settings", plaintext, signer, {
      itemVersion: 1,
      authorDeviceId: 1,
      ts: 100,
    });
    const opened = await openItem(
      rootKey,
      sealed.manifest,
      sealed.blob,
      sealed.signature,
      verifier,
      "settings",
    );
    return bytesEqual(opened, plaintext)
      ? pass(name)
      : fail(
          name,
          `opened ${opened.byteLength} bytes, plaintext ${plaintext.byteLength}`,
        );
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCipherTamperedBlobDetected(): Promise<TestCase> {
  const name =
    "cipher: open with tampered blob → SHA-256 mismatch before AES-GCM-open";
  try {
    const rootKey = freshRootKey();
    const { signer, verifier } = makeMockSignerForDevice(1);
    const sealed = await sealItem(
      rootKey,
      "blockList",
      new TextEncoder().encode("alice"),
      signer,
      { itemVersion: 1, authorDeviceId: 1, ts: 100 },
    );
    // Flip a byte in the middle of the blob.
    const tampered = new Uint8Array(sealed.blob);
    tampered[Math.floor(tampered.byteLength / 2)] ^= 0x01;
    try {
      await openItem(
        rootKey,
        sealed.manifest,
        tampered,
        sealed.signature,
        verifier,
        "blockList",
      );
      return fail(name, "open() succeeded despite tampered blob");
    } catch (e) {
      if (e instanceof CloudSyncCipherError && e.kind === "blobHashMismatch") {
        return pass(name);
      }
      return fail(name, `wrong error kind: ${(e as Error).message}`);
    }
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCipherForgedSignatureRejected(): Promise<TestCase> {
  const name =
    "cipher: open with forged manifest (bad signature) → invalidSignature";
  try {
    const rootKey = freshRootKey();
    const { signer } = makeMockSignerForDevice(1);
    const sealed = await sealItem(
      rootKey,
      "settings",
      new TextEncoder().encode("x"),
      signer,
      { itemVersion: 1, authorDeviceId: 1, ts: 100 },
    );
    try {
      // Use a verifier that rejects everything — same effect as a bad
      // signature value from the cryptographic verifier's perspective.
      await openItem(
        rootKey,
        sealed.manifest,
        sealed.blob,
        sealed.signature,
        rejectAllVerifier,
        "settings",
      );
      return fail(name, "open() succeeded despite forged signature");
    } catch (e) {
      if (
        e instanceof CloudSyncCipherError &&
        e.kind === "invalidSignature"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error kind: ${(e as Error).message}`);
    }
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCipherKindMismatchRejected(): Promise<TestCase> {
  const name =
    "cipher: open with mismatched itemKind manifest vs expected → kindMismatch";
  try {
    const rootKey = freshRootKey();
    const { signer, verifier } = makeMockSignerForDevice(1);
    const sealed = await sealItem(
      rootKey,
      "settings",
      new TextEncoder().encode("x"),
      signer,
      { itemVersion: 1, authorDeviceId: 1, ts: 100 },
    );
    try {
      await openItem(
        rootKey,
        sealed.manifest,
        sealed.blob,
        sealed.signature,
        verifier,
        "blockList", // mismatch
      );
      return fail(name, "open() succeeded despite kind mismatch");
    } catch (e) {
      if (
        e instanceof CloudSyncCipherError &&
        e.kind === "kindMismatch"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error kind: ${(e as Error).message}`);
    }
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// C. Conflict policy
// ---------------------------------------------------------------------------

function makeManifest(
  overrides: Partial<CloudSyncManifest> = {},
): CloudSyncManifest {
  const blobSha256 = new Uint8Array(32);
  // A deterministic non-zero pattern so callers that swap manifests see
  // a non-default hash too.
  for (let i = 0; i < 32; i += 1) blobSha256[i] = (i * 7) & 0xff;
  return {
    itemKind: "settings",
    itemVersion: 1,
    blobSha256,
    authorDeviceId: 1,
    ts: 1_000,
    ...overrides,
  };
}

function testConflictLocalVersionWins(): TestCase {
  const name = "conflict: local v2 > remote v1 → local wins (itemVersion)";
  const local = makeManifest({ itemVersion: 2 });
  const remote = makeManifest({ itemVersion: 1 });
  const r = resolveConflict(local, remote);
  if (r.winner === "local" && r.reason.includes("itemVersion")) {
    return pass(name);
  }
  return fail(name, `got winner=${r.winner} reason=${r.reason}`);
}

function testConflictRemoteVersionWins(): TestCase {
  const name = "conflict: remote v3 > local v2 → remote wins (itemVersion)";
  const local = makeManifest({ itemVersion: 2 });
  const remote = makeManifest({ itemVersion: 3 });
  const r = resolveConflict(local, remote);
  if (r.winner === "remote" && r.reason.includes("itemVersion")) {
    return pass(name);
  }
  return fail(name, `got winner=${r.winner} reason=${r.reason}`);
}

function testConflictTieOnVersionTsLocalWins(): TestCase {
  const name = "conflict: same itemVersion, local ts newer → local wins (ts)";
  const local = makeManifest({ itemVersion: 5, ts: 2_000 });
  const remote = makeManifest({ itemVersion: 5, ts: 1_000 });
  const r = resolveConflict(local, remote);
  if (r.winner === "local" && r.reason.includes("ts")) {
    return pass(name);
  }
  return fail(name, `got winner=${r.winner} reason=${r.reason}`);
}

function testConflictTieOnVersionTsRemoteWins(): TestCase {
  const name = "conflict: same itemVersion, remote ts newer → remote wins (ts)";
  const local = makeManifest({ itemVersion: 5, ts: 1_000 });
  const remote = makeManifest({ itemVersion: 5, ts: 2_000 });
  const r = resolveConflict(local, remote);
  if (r.winner === "remote" && r.reason.includes("ts")) {
    return pass(name);
  }
  return fail(name, `got winner=${r.winner} reason=${r.reason}`);
}

function testConflictDeviceIdTiebreakLocal(): TestCase {
  const name =
    "conflict: same itemVersion+ts → deterministic authorDeviceId ascending (local lower wins)";
  const local = makeManifest({
    itemVersion: 5,
    ts: 1_000,
    authorDeviceId: 1,
  });
  const remote = makeManifest({
    itemVersion: 5,
    ts: 1_000,
    authorDeviceId: 2,
  });
  const r = resolveConflict(local, remote);
  if (r.winner === "local" && r.reason.includes("authorDeviceId")) {
    return pass(name);
  }
  return fail(name, `got winner=${r.winner} reason=${r.reason}`);
}

function testConflictDeviceIdTiebreakRemote(): TestCase {
  const name =
    "conflict: same itemVersion+ts → deterministic authorDeviceId ascending (remote lower wins)";
  const local = makeManifest({
    itemVersion: 5,
    ts: 1_000,
    authorDeviceId: 3,
  });
  const remote = makeManifest({
    itemVersion: 5,
    ts: 1_000,
    authorDeviceId: 2,
  });
  const r = resolveConflict(local, remote);
  if (r.winner === "remote" && r.reason.includes("authorDeviceId")) {
    return pass(name);
  }
  return fail(name, `got winner=${r.winner} reason=${r.reason}`);
}

function testConflictIdenticalManifests(): TestCase {
  const name =
    "conflict: identical (itemVersion, ts, authorDeviceId) → local wins (identical)";
  const local = makeManifest({
    itemVersion: 5,
    ts: 1_000,
    authorDeviceId: 1,
  });
  const remote = makeManifest({
    itemVersion: 5,
    ts: 1_000,
    authorDeviceId: 1,
  });
  const r = resolveConflict(local, remote);
  if (r.winner === "local" && r.reason === "identical-manifests") {
    return pass(name);
  }
  return fail(name, `got winner=${r.winner} reason=${r.reason}`);
}

// ---------------------------------------------------------------------------
// D. Store
// ---------------------------------------------------------------------------

async function testStoreApplyRemoteAndCurrentBlob(): Promise<TestCase> {
  const name =
    "store: applyRemote then currentBlob retrieves identical bytes";
  try {
    const rootKey = freshRootKey();
    const { signer } = makeMockSignerForDevice(1);
    const sealed = await sealItem(
      rootKey,
      "settings",
      new TextEncoder().encode("settings-blob"),
      signer,
      { itemVersion: 1, authorDeviceId: 1, ts: 100 },
    );
    const store = freshStore();
    await store.applyRemote(
      "settings",
      sealed.manifest,
      sealed.blob,
      sealed.signature,
    );
    const got = await store.currentBlob("settings");
    if (!got || !bytesEqual(got, sealed.blob)) {
      return fail(name, "currentBlob did not match the applied blob");
    }
    const gotManifest = await store.currentManifest("settings");
    if (!gotManifest || gotManifest.itemVersion !== 1) {
      return fail(
        name,
        `currentManifest mismatch: ${JSON.stringify(gotManifest)}`,
      );
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testStoreClearRemovesEntry(): Promise<TestCase> {
  const name = "store: clear removes the entry";
  try {
    const rootKey = freshRootKey();
    const { signer } = makeMockSignerForDevice(1);
    const sealed = await sealItem(
      rootKey,
      "blockList",
      new TextEncoder().encode("blob"),
      signer,
      { itemVersion: 1, authorDeviceId: 1, ts: 100 },
    );
    const store = freshStore();
    await store.recordLocal(
      "blockList",
      sealed.manifest,
      sealed.blob,
      sealed.signature,
    );
    await store.clear("blockList");
    const m = await store.currentManifest("blockList");
    const b = await store.currentBlob("blockList");
    if (m === null && b === null) {
      return pass(name);
    }
    return fail(
      name,
      `after clear: manifest=${JSON.stringify(m)} blob=${b ? b.byteLength : "null"}`,
    );
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testStoreSharedAcrossInstances(): Promise<TestCase> {
  const name =
    "store: two CloudSyncStore instances on the same DB name share state";
  try {
    const rootKey = freshRootKey();
    const { signer } = makeMockSignerForDevice(1);
    const sealed = await sealItem(
      rootKey,
      "contactRoster",
      new TextEncoder().encode("alice,bob"),
      signer,
      { itemVersion: 1, authorDeviceId: 1, ts: 100 },
    );
    const dbName = `kaether:cloud-sync:selftest:shared:${dbCounter}:${Date.now()}`;
    dbCounter += 1;
    const writer = new CloudSyncStore(dbName);
    await writer.recordLocal(
      "contactRoster",
      sealed.manifest,
      sealed.blob,
      sealed.signature,
    );
    const reader = new CloudSyncStore(dbName);
    const blob = await reader.currentBlob("contactRoster");
    if (!blob || !bytesEqual(blob, sealed.blob)) {
      return fail(name, "second instance did not see the first's write");
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Cloud sync (manifest + cipher + conflict)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [
      // A. Manifest canonical bytes
      await testCanonicalBytesStable(),
      await testCanonicalBytesDifferOnItemKind(),
      await testCanonicalBytesDifferOnItemVersion(),
      await testCanonicalBytesDifferOnBlobSha(),
      await testCanonicalBytesDifferOnTs(),
      await testCanonicalBytesBoundedBySha256(),
      // B. Cipher
      await testCipherRoundTrip(),
      await testCipherTamperedBlobDetected(),
      await testCipherForgedSignatureRejected(),
      await testCipherKindMismatchRejected(),
      // C. Conflict policy
      testConflictLocalVersionWins(),
      testConflictRemoteVersionWins(),
      testConflictTieOnVersionTsLocalWins(),
      testConflictTieOnVersionTsRemoteWins(),
      testConflictDeviceIdTiebreakLocal(),
      testConflictDeviceIdTiebreakRemote(),
      testConflictIdenticalManifests(),
      // D. Store
      await testStoreApplyRemoteAndCurrentBlob(),
      await testStoreClearRemovesEntry(),
      await testStoreSharedAcrossInstances(),
    ];
    return buildSuiteResult(cases);
  },
});
